import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Trash2, Pencil, Calendar, X, CheckSquare, Square, FolderPlus, Folder, Settings as SettingsIcon, ChevronDown } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import { useTranslation } from '../lib/i18n';
import { formatDate as formatDateIn } from '../lib/dates';
import { useConfirm, usePrompt } from '../lib/ConfirmDialog';
import { defaultData } from '../utils/storage';
import { playBlub } from '../lib/sounds';
import { PRIORITIES, priorityRank } from '../lib/priorities';
import { loadProfile } from '../lib/profile';
import { listSharedCalendarMembers } from '../lib/sharedCalendars';

const VIEWS = [
  { id: 'today', labelKey: 'todo.viewToday' },
  { id: 'all', labelKey: 'todo.viewAll' },
  { id: 'completed', labelKey: 'todo.viewCompleted' }
];

const emptyDraft = () => ({
  id: null,
  title: '',
  date: '',
  time: '',
  priority: '',
  note: '',
  groupId: ''
});

// Tasks are ordered by date, then time, then priority (high first). Items
// without a date sort last; within a day, timed items come before untimed
// ones so the day reads like a schedule.
const byDateTimePriority = (a, b) => {
  const da = a.date || '';
  const db = b.date || '';
  if (da !== db) {
    if (!da) return 1;
    if (!db) return -1;
    return da.localeCompare(db);
  }
  const ta = a.time || '';
  const tb = b.time || '';
  if (ta !== tb) {
    if (!ta) return 1;
    if (!tb) return -1;
    return ta.localeCompare(tb);
  }
  const byPrio = priorityRank(a.priority) - priorityRank(b.priority);
  if (byPrio) return byPrio;
  return (b.createdAt || '').localeCompare(a.createdAt || '');
};

const Todo = ({ data, updateData }) => {
  const { t, lang } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const tasks = data.todo?.tasks || [];
  const groups = data.todo?.groups || [];
  const [view, setView] = useState('today');
  const [selectedGroupId, setSelectedGroupId] = useState(''); // '' = all groups
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [showManageGroups, setShowManageGroups] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  // Display names for the colour picker's "belegt" notes ("Janas To-dos").
  const [myName, setMyName] = useState('');
  const [memberNames, setMemberNames] = useState({});
  useEffect(() => {
    let cancelled = false;
    loadProfile()
      .then((p) => {
        if (!cancelled) setMyName((p && p.displayName) || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Calendar link — same pattern as the birthday calendar: a toggle, a
  // target calendar (personal or one of the shared calendars) and a label
  // color; the Calendar page derives events from the tasks directly. When a
  // shared calendar is chosen the tasks are stamped with its id so members
  // can see them (read-only) in that calendar.
  const calendarSettings = data.todo?.calendarSettings || {
    linkedToCalendar: false,
    categoryId: 'blue',
    sharedCalendarId: null
  };
  const sharedCalendars = data.calendar?.sharedCalendars || [];

  // Label options scoped to the TARGET calendar: custom renames apply, the
  // birthday-reserved color shows as "Birthday", and free colors sort first.
  const labelOptions = useMemo(() => {
    const scopeId = calendarSettings.sharedCalendarId || null;
    const scoped = (data.calendar?.categories || []).filter(
      (c) => (c.sharedCalendarId || null) === scopeId
    );
    const byId = new Map(scoped.map((c) => [c.id, c]));
    // Overlay scope rows on the defaults so all 13 colors are always shown
    // (shared calendars only persist customized rows).
    const base = defaultData.calendar.categories.map((d) => byId.get(d.id) || d);
    let birthdayCatId = null;
    if (scopeId) {
      const cal = sharedCalendars.find((c) => c.id === scopeId);
      if (cal?.hasBirthdays && cal.birthdayLinked !== false) {
        birthdayCatId = cal.birthdayCategoryId || 'purple';
      }
    } else {
      const bs = data.calendar?.birthdaySettings;
      if (bs?.linkedToCalendar) birthdayCatId = bs.categoryId || 'purple';
    }
    // Colours already occupied by someone's to-dos in the target calendar.
    const todoOwners = new Map();
    if (calendarSettings.linkedToCalendar && calendarSettings.categoryId) {
      todoOwners.set(calendarSettings.categoryId, myName);
    }
    if (scopeId) {
      for (const tk of [...(data.todo?.tasks || []), ...(data.todo?.sharedTasks || [])]) {
        if (tk.sharedCalendarId !== scopeId) continue;
        const cid = tk.calendarCategoryId;
        if (!cid || todoOwners.has(cid)) continue;
        todoOwners.set(cid, (tk.createdBy && memberNames[tk.createdBy]) || '');
      }
    }
    const decorated = base.map((c) => {
      if (c.id === birthdayCatId) {
        return { ...c, displayName: 'Birthday', taken: true };
      }
      if (todoOwners.has(c.id)) {
        const owner = todoOwners.get(c.id);
        return {
          ...c,
          displayName: owner
            ? t('calendar.todosOf', '{name}s To-dos', { name: owner })
            : t('todo.title'),
          taken: true
        };
      }
      if (c.isCustom) return { ...c, displayName: c.name, taken: true };
      return { ...c, displayName: c.name, taken: false };
    });
    return [
      ...decorated.filter((c) => !c.taken),
      ...decorated.filter((c) => c.taken)
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.calendar?.categories,
    data.calendar?.birthdaySettings,
    data.todo?.tasks,
    data.todo?.sharedTasks,
    sharedCalendars,
    calendarSettings.sharedCalendarId,
    calendarSettings.linkedToCalendar,
    calendarSettings.categoryId,
    myName,
    memberNames
  ]);
  const selectedCategory =
    labelOptions.find((c) => c.id === calendarSettings.categoryId) || labelOptions[0];

  // Settings changes restamp every task so the calendar target, the label
  // color and the member-visibility (shared_calendar_id) stay in sync on
  // the rows themselves — that's what other members' calendars read.
  const updateCalendarSettings = (patch) => {
    const nextSettings = { ...calendarSettings, ...patch };
    const target = nextSettings.linkedToCalendar
      ? nextSettings.sharedCalendarId || null
      : null;
    updateData({
      todo: {
        ...(data.todo || {}),
        tasks: tasks.map((task) => ({
          ...task,
          sharedCalendarId: target,
          calendarCategoryId: nextSettings.categoryId || null
        })),
        groups,
        calendarSettings: nextSettings
      }
    });
  };

  const targetCalendarId = calendarSettings.sharedCalendarId || null;
  useEffect(() => {
    if (!targetCalendarId) return undefined;
    let cancelled = false;
    listSharedCalendarMembers(targetCalendarId)
      .then((rows) => {
        if (cancelled) return;
        const map = {};
        for (const m of rows || []) map[m.userId] = m.displayName || '';
        setMemberNames(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [targetCalendarId]);

  const saveTasks = (next) => {
    updateData({ todo: { ...(data.todo || {}), tasks: next, groups } });
  };
  const saveGroups = (next) => {
    updateData({ todo: { ...(data.todo || {}), tasks, groups: next } });
  };

  const createGroup = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const existing = groups.find(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) return existing.id;
    const id = 'g-' + Date.now().toString();
    saveGroups([...groups, { id, name: trimmed }]);
    return id;
  };

  const renameGroup = (id, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    saveGroups(groups.map((g) => (g.id === id ? { ...g, name: trimmed } : g)));
  };

  const deleteGroup = async (id) => {
    if (!(await confirm({ message: t('todo.deleteGroupConfirm'), danger: true }))) return;
    saveGroups(groups.filter((g) => g.id !== id));
    // Un-assign tasks from this group
    updateData({
      todo: {
        ...(data.todo || {}),
        tasks: tasks.map((t) => (t.groupId === id ? { ...t, groupId: null } : t)),
        groups: groups.filter((g) => g.id !== id)
      }
    });
    if (selectedGroupId === id) setSelectedGroupId('');
  };

  const promptNewGroup = async () => {
    const name = await prompt({
      title: t('todo.groupNamePrompt'),
      placeholder: t('todo.newGroup'),
      confirmText: t('common.add')
    });
    if (!name) return null;
    return createGroup(name);
  };

  const openNew = () => {
    setDraft({ ...emptyDraft(), groupId: selectedGroupId || '' });
    setShowForm(true);
  };

  const openEdit = (task) => {
    setDraft({
      id: task.id,
      title: task.title || '',
      date: task.date || '',
      time: task.time || '',
      priority: task.priority || '',
      note: task.note || '',
      groupId: task.groupId || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setDraft(emptyDraft());
  };

  const saveDraft = () => {
    if (!draft.title.trim()) return;
    const payload = {
      title: draft.title.trim(),
      date: draft.date || null,
      time: (draft.date && draft.time) || null,
      priority: draft.priority || null,
      note: draft.note.trim() || null,
      groupId: draft.groupId || null,
      // Stamp the current calendar link onto the row so shared-calendar
      // members see it in the right calendar and color.
      sharedCalendarId: calendarSettings.linkedToCalendar
        ? calendarSettings.sharedCalendarId || null
        : null,
      calendarCategoryId: calendarSettings.categoryId || null
    };

    if (draft.id) {
      saveTasks(
        tasks.map((t) =>
          t.id === draft.id ? { ...t, ...payload } : t
        )
      );
    } else {
      const newTask = {
        id: Date.now().toString(),
        done: false,
        createdAt: new Date().toISOString(),
        ...payload
      };
      saveTasks([newTask, ...tasks]);
    }
    closeForm();
  };

  const toggleDone = (id) => {
    const target = tasks.find((t) => t.id === id);
    // Gooey blub on completing (not on re-opening).
    if (target && !target.done) playBlub();
    saveTasks(
      tasks.map((t) =>
        t.id === id
          ? {
              ...t,
              done: !t.done,
              completedAt: !t.done ? new Date().toISOString() : null
            }
          : t
      )
    );
  };

  const deleteTask = async (id) => {
    if (!(await confirm({ message: t('todo.confirmDelete'), danger: true }))) return;
    saveTasks(tasks.filter((task) => task.id !== id));
  };

  // Auto-purge tasks completed more than 24h ago. Runs once on mount and
  // every minute thereafter so a task that turns 24h old while the page is
  // open also disappears.
  // NOTE: the tick must write through a ref of the WHOLE todo section, not
  // the render-time `saveTasks` closure — that closure captures mount-time
  // groups/settings and would revert (and via the sync diff, DELETE) any
  // group or setting changed after mount.
  const todoRef = useRef(data.todo);
  todoRef.current = data.todo;
  useEffect(() => {
    const tick = () => {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const cur = todoRef.current?.tasks || [];
      const next = cur.filter((task) => {
        if (!task.done) return true;
        const ts = task.completedAt ? Date.parse(task.completedAt) : NaN;
        if (isNaN(ts)) return true; // missing timestamp -> keep
        return ts >= cutoff;
      });
      if (next.length !== cur.length) {
        updateData({ todo: { ...(todoRef.current || {}), tasks: next } });
      }
    };
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Heute / Morgen / dd.mm.yyyy — the day headers in the "Alle" list.
  const sectionLabel = (iso) => {
    if (!iso) return t('todo.noDate');
    try {
      const d = parseISO(iso);
      if (isToday(d)) return t('common.today');
      if (isTomorrow(d)) return t('common.tomorrow');
      return format(d, 'dd.MM.yyyy');
    } catch {
      return iso;
    }
  };

  const filtered = useMemo(() => {
    const base = selectedGroupId
      ? tasks.filter((t) => t.groupId === selectedGroupId)
      : tasks;
    let list;
    switch (view) {
      case 'today':
        list = base.filter(
          (t) => !t.done && t.date && isToday(parseISO(t.date))
        );
        break;
      case 'completed':
        list = base.filter((t) => t.done);
        break;
      case 'all':
      default:
        list = base;
        break;
    }
    return [...list].sort(byDateTimePriority);
  }, [tasks, view, selectedGroupId]);

  // "Alle" is grouped into one section per day (Heute / Morgen / dd.mm.yyyy,
  // undated last), each internally sorted the same way as the flat views.
  const sections = useMemo(() => {
    if (view !== 'all') return null;
    const buckets = new Map();
    for (const task of filtered) {
      const key = task.date || '';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(task);
    }
    return [...buckets.entries()]
      .sort((a, b) => {
        if (a[0] === b[0]) return 0;
        if (!a[0]) return 1; // undated bucket last
        if (!b[0]) return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([key, items]) => ({ key: key || 'nodate', label: sectionLabel(key), tasks: items }));
  }, [filtered, view, lang]);

  const counts = useMemo(() => {
    const base = selectedGroupId
      ? tasks.filter((t) => t.groupId === selectedGroupId)
      : tasks;
    const today = base.filter(
      (t) => !t.done && t.date && isToday(parseISO(t.date))
    ).length;
    const all = base.length;
    const completed = base.filter((t) => t.done).length;
    return { today, all, completed };
  }, [tasks, selectedGroupId]);

  const groupById = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = g;
    return m;
  }, [groups]);

  const formatDate = (iso) => formatDateIn(iso, 'medium', lang) || iso;

  // One task row — shared by the flat lists (Heute / Erledigt) and the
  // day-sectioned "Alle" list.
  const renderTask = (task) => {
  const prio = PRIORITIES.find((p) => p.id === task.priority);
  const overdue =
    !task.done &&
    task.date &&
    isPast(parseISO(task.date)) &&
    !isToday(parseISO(task.date));
  return (
    <li
      key={task.id}
      className={`group flex items-start gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow ${
        task.done ? 'opacity-60' : ''
      }`}
    >
      <button
        onClick={() => toggleDone(task.id)}
        className="mt-0.5 text-gray-400 hover:text-primary-600"
        title={task.done ? t('todo.markOpen') : t('todo.markDone')}
      >
        {task.done ? (
          <CheckSquare size={20} className="text-primary-600" />
        ) : (
          <Square size={20} />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <p
            className={`text-sm font-medium flex-1 ${
              task.done ? 'line-through text-gray-400' : 'text-gray-900'
            }`}
          >
            {task.title}
          </p>
          {prio && (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded border ${prio.color} flex items-center gap-1 flex-shrink-0`}
            >
              <span className="leading-none">{prio.emoji}</span>
              {t(prio.labelKey)}
            </span>
          )}
        </div>

        {(task.date || task.note || task.groupId) && (
          <div className="mt-1 space-y-1">
            {task.groupId && groupById[task.groupId] && !selectedGroupId && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Folder size={12} />
                {groupById[task.groupId].name}
              </div>
            )}
            {task.date && (
              <div
                className={`flex items-center gap-1 text-xs ${
                  overdue ? 'text-red-600 font-medium' : 'text-gray-500'
                }`}
              >
                <Calendar size={12} />
                {formatDate(task.date)}
                {task.time && <span>· {task.time}</span>}
                {overdue && <span>· {t('todo.overdue')}</span>}
              </div>
            )}
            {task.note && (
              <p className="text-xs text-gray-500 whitespace-pre-wrap">
                {task.note}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => openEdit(task)}
          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
          title={t('common.edit')}
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={() => deleteTask(task.id)}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          title={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('todo.title')}</h2>
          <p className="text-sm sm:text-base text-gray-500">{t('todo.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCalendarSettings(true)}
          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 flex-shrink-0"
          title={t('todo.showInCalendar')}
          aria-label={t('todo.showInCalendar')}
        >
          <SettingsIcon size={18} />
        </button>
      </header>

      {/* View tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {VIEWS.map((v) => {
          const count = counts[v.id] || 0;
          const active = view === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                active
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(v.labelKey)}
              <span
                className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                  active ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Group chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setSelectedGroupId('')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            selectedGroupId === ''
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          {t('todo.allGroups')}
        </button>
        {groups.map((g) => (
          <button
            key={g.id}
            onClick={() =>
              setSelectedGroupId((curr) => (curr === g.id ? '' : g.id))
            }
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              selectedGroupId === g.id
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Folder size={12} />
            {g.name}
          </button>
        ))}
        <button
          onClick={async () => {
            const id = await promptNewGroup();
            if (id) setSelectedGroupId(id);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600"
        >
          <FolderPlus size={12} />
          {t('todo.newGroup')}
        </button>
        {groups.length > 0 && (
          <button
            onClick={() => setShowManageGroups(true)}
            className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium text-gray-400 hover:text-gray-700"
            title={t('todo.manageGroups')}
          >
            <SettingsIcon size={14} />
          </button>
        )}
      </div>

      {/* Add button */}
      {!showForm && (
        <button
          onClick={openNew}
          className="fixed safe-bottom-fab right-6 z-30 w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 active:scale-95 transition-all flex items-center justify-center"
          title={t('todo.newTitle')}
          aria-label={t('todo.newTitle')}
        >
          <Plus size={28} />
        </button>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {draft.id ? t('todo.editTitle') : t('todo.newTitle')}
            </h3>
            <button onClick={closeForm} className="p-1 hover:bg-gray-100 rounded">
              <X size={18} />
            </button>
          </div>

          <input
            autoFocus
            type="text"
            placeholder={t('todo.newTaskPlaceholder')}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                saveDraft();
              }
            }}
            className="input"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('todo.dateOptional')}
              </label>
              <input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('todo.timeOptional')}
              </label>
              <input
                type="time"
                value={draft.time}
                onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                disabled={!draft.date}
                className="input disabled:opacity-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('todo.priorityOptional')}
            </label>
            <div className="flex gap-1">
              {PRIORITIES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      priority: draft.priority === p.id ? '' : p.id
                    })
                  }
                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    draft.priority === p.id
                      ? p.color
                      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="leading-none mr-1">{p.emoji}</span>
                  {t(p.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('todo.groupOptional')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setDraft({ ...draft, groupId: '' })}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                  !draft.groupId
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {t('todo.noGroup')}
              </button>
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setDraft({ ...draft, groupId: g.id })}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                    draft.groupId === g.id
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Folder size={10} />
                  {g.name}
                </button>
              ))}
              <button
                type="button"
                onClick={async () => {
                  const id = await promptNewGroup();
                  if (id) setDraft((d) => ({ ...d, groupId: id }));
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:text-primary-600 hover:border-primary-400"
              >
                <FolderPlus size={10} />
                {t('todo.newGroupShort')}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('todo.noteOptional')}
            </label>
            <textarea
              rows={3}
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              className="input resize-none"
              placeholder={t('todo.notePlaceholder')}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={closeForm} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              onClick={saveDraft}
              disabled={!draft.title.trim()}
              className="btn-primary disabled:opacity-50"
            >
              {draft.id ? t('common.save') : t('common.add')}
            </button>
          </div>
        </div>
      )}

      {/* Task list */}
      {filtered.length === 0 ? (
        <div className="card text-center py-12">
          <CheckSquare size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            {view === 'today' && t('todo.emptyToday')}
            {view === 'upcoming' && t('todo.emptyUpcoming')}
            {view === 'all' && t('todo.emptyAll')}
            {view === 'completed' && t('todo.emptyCompleted')}
          </p>
        </div>
      ) : sections ? (
        <div className="space-y-4">
          {sections.map((section, idx) => (
            <div key={section.key}>
              <div
                className={`flex items-baseline gap-2 mb-2 ${
                  idx > 0 ? 'pt-4 border-t border-gray-200' : ''
                }`}
              >
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {section.label}
                </h3>
                <span className="text-xs text-gray-400">{section.tasks.length}</span>
              </div>
              <ul className="space-y-2">{section.tasks.map(renderTask)}</ul>
            </div>
          ))}
        </div>
      ) : (
        <ul className="space-y-2">{filtered.map(renderTask)}</ul>
      )}

      {/* Calendar link settings (gear in the header) */}
      {showCalendarSettings && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { setShowCalendarSettings(false); setShowColorPicker(false); }}
        >
          <div
            className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-visible flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('todo.showInCalendar')}</h3>
              <button
                onClick={() => { setShowCalendarSettings(false); setShowColorPicker(false); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar size={18} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {t('todo.showInCalendar')}
              </span>
            </div>
            <button
              onClick={() =>
                updateCalendarSettings({
                  linkedToCalendar: !calendarSettings.linkedToCalendar
                })
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                calendarSettings.linkedToCalendar ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  calendarSettings.linkedToCalendar ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {calendarSettings.linkedToCalendar && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-600">{t('todo.calendarTarget')}</span>
                <select
                  value={calendarSettings.sharedCalendarId || ''}
                  onChange={(e) =>
                    updateCalendarSettings({ sharedCalendarId: e.target.value || null })
                  }
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 max-w-[60%]"
                >
                  <option value="">{t('todo.personalCalendar')}</option>
                  {sharedCalendars.map((cal) => (
                    <option key={cal.id} value={cal.id}>
                      {cal.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('todo.labelColor')}</span>
                <div className="relative">
                  <button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                  >
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: selectedCategory?.color }}
                    />
                    <span className="font-medium" style={{ color: selectedCategory?.color }}>
                      Todo
                    </span>
                    <ChevronDown size={14} className="text-gray-400" />
                  </button>
                  {showColorPicker && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 z-20 w-56">
                      {labelOptions.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            updateCalendarSettings({ categoryId: cat.id });
                            setShowColorPicker(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors ${
                            cat.id === calendarSettings.categoryId ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="truncate" style={{ color: cat.color }}>
                            {cat.displayName}
                          </span>
                          {cat.taken && (
                            <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400 flex-shrink-0">
                              {t('todo.inUse')}
                            </span>
                          )}
                          {cat.id === calendarSettings.categoryId && (
                            <span className={`${cat.taken ? '' : 'ml-auto '}text-blue-600 flex-shrink-0`}>✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400">
                {t('todo.calendarHint')}
                {calendarSettings.sharedCalendarId && ` ${t('todo.calendarSharedHint')}`}
              </p>
            </div>
          )}
            </div>
          </div>
        </div>
      )}

      {/* Manage Groups Modal */}
      {showManageGroups && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowManageGroups(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('todo.manageGroups')}</h3>
              <button
                onClick={() => setShowManageGroups(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2">
              {groups.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  {t('todo.noGroups')}
                </p>
              ) : (
                groups.map((g) => {
                  const taskCount = tasks.filter((t) => t.groupId === g.id).length;
                  return (
                    <div
                      key={g.id}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                    >
                      <Folder size={14} className="text-gray-500" />
                      <input
                        type="text"
                        defaultValue={g.name}
                        onBlur={(e) => {
                          if (e.target.value.trim() && e.target.value !== g.name) {
                            renameGroup(g.id, e.target.value);
                          }
                        }}
                        className="flex-1 bg-transparent text-sm font-medium text-gray-800 outline-none focus:bg-white focus:border focus:border-primary-500 focus:px-2 focus:py-1 focus:rounded"
                      />
                      <span className="text-xs text-gray-400">
                        {taskCount} {taskCount === 1 ? t('todo.tasksOne') : t('todo.tasksOther')}
                      </span>
                      <button
                        onClick={() => deleteGroup(g.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              )}
              <button
                onClick={() => promptNewGroup()}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600 text-sm"
              >
                <FolderPlus size={14} />
                {t('todo.newGroup')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Todo;
